import { Localization } from "@viamap/viamap2-common";
import type { LLMAnalysisResultItem } from "./ANNLLMAnalyzer";
import {
   AnnotationType,
   type ANNAnnotationRecord,
   type ANNDocumentLlmAnalysis,
   type ANNDocumentPayload,
} from "./ANNTypes";

function normalizeTextValue(value?: string): string {
   return (value || "").trim().toLowerCase();
}

export function normalizeAnnotationTypeValue(value?: string): string {
   return normalizeTextValue(value);
}

export function normalizeDatoloebenrValue(value?: string): string {
   return normalizeTextValue(value);
}

export function normalizePages(pages: number[]): number[] {
   return Array.from(new Set(pages))
      .filter((page) => Number.isFinite(page) && page > 0)
      .map((page) => Math.trunc(page))
      .sort((a, b) => a - b);
}

/** Keep only 1-based pages that exist in a PDF of `numPages` pages. */
export function clampPagesToDocument(pages: number[], numPages: number): number[] {
   if (!Number.isFinite(numPages) || numPages < 1) {
      return [];
   }
   const last = Math.trunc(numPages);
   return normalizePages(pages).filter((page) => page <= last);
}

export function arePageSelectionsEqual(a: number[], b: number[]): boolean {
   const normalizedA = normalizePages(a);
   const normalizedB = normalizePages(b);
   return normalizedA.length === normalizedB.length && normalizedA.every((page, index) => page === normalizedB[index]);
}

/**
 * Closing the PDF viewer always writes back the current page set.
 * That must not undo phase-A Ja when the set did not actually change —
 * imported PDFs have no AI page list, so "differs from AI" is always true.
 */
export function viewerCloseKeepsPageApproval(
   previousSelected: number[] | undefined,
   persistedPages: number[],
): boolean {
   const previous = normalizePages(previousSelected || []);
   if (!previous.length) {
      return true;
   }
   return arePageSelectionsEqual(previous, persistedPages);
}

export function buildConsecutivePages(count: number): number[] {
   if (!Number.isFinite(count) || count < 1) {
      return [];
   }
   return Array.from({ length: Math.trunc(count) }, (_, index) => index + 1);
}

/**
 * Digital/newer servituts are analyzed as a whole document, so the AI often
 * returns an empty pages_kept list. Only then should empty mean every page.
 */
export function resolveAnalysisRelevantPages(
   pagesKept: number[] | undefined,
   totalPages?: number,
   emptyMeansAll = false,
): number[] {
   const normalized = normalizePages(pagesKept || []);
   if (normalized.length > 0) {
      return normalized;
   }
   if (!emptyMeansAll) {
      return [];
   }
   return buildConsecutivePages(totalPages || 0);
}

export function formatPagesList(pages: number[]): string {
   return normalizePages(pages).join(", ");
}

export type PdfSourceKind = "fullAkt" | "cutAnnotation";

/** True when the selection is every page of a cut annotation PDF (1..numPages). */
export function isFullCutAnnotationSelection(
   selectedPages: number[],
   cutPageCount: number,
): boolean {
   if (!cutPageCount || cutPageCount < 1) {
      return false;
   }
   const expected = normalizePages(
      Array.from({ length: cutPageCount }, (_, index) => index + 1),
   );
   return arePageSelectionsEqual(selectedPages, expected);
}

/**
 * Map 1-based page indices of a cut annotation PDF back to original akt page numbers.
 * `sourceExtractedPages` is ordered as the cut PDF pages (cut page i → sourceExtractedPages[i-1]).
 */
export function mapCutSelectionToAktPages(
   selectedCutPages: number[],
   sourceExtractedPages: number[],
): number[] | null {
   if (!sourceExtractedPages.length) {
      return null;
   }
   const mapped: number[] = [];
   for (const cutPage of normalizePages(selectedCutPages)) {
      const aktPage = sourceExtractedPages[cutPage - 1];
      if (!Number.isFinite(aktPage) || aktPage <= 0) {
         return null;
      }
      mapped.push(aktPage);
   }
   return normalizePages(mapped);
}

/** Map original akt page numbers to 1-based indices in a cut annotation PDF. */
export function mapAktPagesToCutSelection(
   aktPages: number[],
   sourceExtractedPages: number[],
): number[] | null {
   if (!sourceExtractedPages.length) {
      return null;
   }
   const mapped: number[] = [];
   for (const aktPage of normalizePages(aktPages)) {
      const cutIndex = sourceExtractedPages.indexOf(aktPage);
      if (cutIndex < 0) {
         return null;
      }
      mapped.push(cutIndex + 1);
   }
   return normalizePages(mapped);
}

export function getAnnotationSourceExtractedPages(row: {
   annotationDocument?: { extractedPages?: number[] } | null;
   selectedPages?: number[];
   analysisItem?: { pages_kept?: number[] } | null;
}): number[] {
   return normalizePages(
      row.annotationDocument?.extractedPages
         || row.selectedPages
         || row.analysisItem?.pages_kept
         || [],
   );
}

/** True when pages are exactly 1..N (a whole-document selection). */
export function isConsecutivePageRange(pages: number[]): boolean {
   const normalized = normalizePages(pages);
   return normalized.length > 0
      && normalized[0] === 1
      && normalized.every((page, index) => page === index + 1);
}

/**
 * Akt page numbers to open the PDF viewer with.
 * Older scanned akts must not start from a whole-document selection that
 * disagrees with the AI page list. Empty pages_kept means "none" there, not "all".
 */
export function getViewerInitialAktPages(row: {
   handling?: string;
   documentSource?: string;
   selectedPages?: number[];
   analysisItem?: { pages_kept?: number[] } | null;
}): number[] {
   const analyzed = normalizePages(row.analysisItem?.pages_kept || []);
   const selected = normalizePages(row.selectedPages || []);
   const isElectronic = (row.handling ?? "legacyLlmPdf") === "electronicZipPackage";
   const hasAiPageSearch = Array.isArray(row.analysisItem?.pages_kept);
   // Before page-find, keep the uploaded file's full-page selection.
   // After AI has run, treat it like a scanned akt so a leftover 1..N
   // selection does not hide the actual page list.
   if (row.documentSource === "customUpload" && !hasAiPageSearch) {
      return selected.length ? selected : analyzed;
   }
   if (!isElectronic && analyzed.length > 0 && isConsecutivePageRange(selected) && selected.length > analyzed.length) {
      return analyzed;
   }
   if (row.documentSource === "customUpload" && hasAiPageSearch && analyzed.length === 0 && isConsecutivePageRange(selected)) {
      return [];
   }
   return selected.length ? selected : analyzed;
}

/**
 * Page numbers to send to evaluate-document for a row.
 * Annotation rows use a cut PDF, so akt page numbers must be remapped to local 1..N.
 */
export function getEvaluatePagesForRow(row: {
   documentSource?: string;
   annotationDocument?: { extractedPages?: number[] } | null;
   selectedPages?: number[];
   analysisItem?: { pages_kept?: number[] } | null;
}, preferredAktPages?: number[]): number[] | undefined {
   const preferred = preferredAktPages?.length
      ? normalizePages(preferredAktPages)
      : (row.selectedPages?.length ? normalizePages(row.selectedPages) : undefined);

   if (row.documentSource !== "annotation") {
      return preferred;
   }

   const sourceExtractedPages = getAnnotationSourceExtractedPages(row);
   if (!sourceExtractedPages.length) {
      return preferred;
   }

   if (preferred?.length) {
      const mapped = mapAktPagesToCutSelection(preferred, sourceExtractedPages);
      if (mapped?.length) {
         return mapped;
      }
      // Preferred pages already look like cut-local indices covering the cut PDF.
      if (isFullCutAnnotationSelection(preferred, sourceExtractedPages.length)
         || preferred.every((page) => page >= 1 && page <= sourceExtractedPages.length)) {
         return preferred;
      }
   }

   return sourceExtractedPages.map((_, index) => index + 1);
}

/**
 * Resolve the akt-page numbers that should be persisted / compared for a save.
 * For cut annotation PDFs, viewer indices are remapped via sourceExtractedPages.
 */
export function resolveAktPagesForSave(params: {
   pdfSourceKind?: PdfSourceKind;
   selectedPages: number[];
   sourceExtractedPages?: number[];
   cutPageCount?: number;
}): {
   aktPages: number[];
   isSameAsExistingCut: boolean;
} {
   const selected = normalizePages(params.selectedPages);
   if (params.pdfSourceKind !== "cutAnnotation") {
      return { aktPages: selected, isSameAsExistingCut: false };
   }

   const sourcePages = normalizePages(params.sourceExtractedPages || []);
   const cutPageCount = params.cutPageCount || sourcePages.length;
   if (cutPageCount > 0 && isFullCutAnnotationSelection(selected, cutPageCount)) {
      // Re-saving the entire cut annotation PDF is a no-op (same document),
      // even when original akt page numbers are unknown.
      return {
         aktPages: sourcePages.length === cutPageCount ? sourcePages : (sourcePages.length ? sourcePages : selected),
         isSameAsExistingCut: true,
      };
   }

   const mapped = mapCutSelectionToAktPages(selected, sourcePages);
   return {
      aktPages: mapped || selected,
      isSameAsExistingCut: false,
   };
}

/**
 * Pages to write back when the PDF viewer closes.
 * Viewer-local indices are remapped to akt pages for cut annotations.
 * Do not clamp remapped akt pages to the cut PDF page count — those numbers
 * can be larger than the clipped file (e.g. akt 12, 20 in a 2-page cut).
 */
export function resolvePagesToPersistOnViewerClose(params: {
   pdfSourceKind?: PdfSourceKind;
   selectedViewerPages: number[];
   sourceExtractedPages?: number[];
   numPages: number;
   treatEmptyPagesKeptAsAll?: boolean;
}): number[] {
   const selectedViewerPages = clampPagesToDocument(
      normalizePages(params.selectedViewerPages),
      params.numPages,
   );
   const { aktPages } = resolveAktPagesForSave({
      pdfSourceKind: params.pdfSourceKind,
      selectedPages: selectedViewerPages,
      sourceExtractedPages: params.sourceExtractedPages,
      cutPageCount: params.numPages || (params.sourceExtractedPages?.length ?? 0),
   });

   if (params.pdfSourceKind === "cutAnnotation") {
      return normalizePages(aktPages);
   }

   let persisted = clampPagesToDocument(aktPages, params.numPages);
   if (!persisted.length && params.treatEmptyPagesKeptAsAll) {
      persisted = buildConsecutivePages(params.numPages);
   }
   return persisted;
}

/**
 * Normalize viewer write-back pages into the row's akt-page space.
 * After a cut annotation save, the viewer uses 1..N of the clipped PDF.
 */
export function viewerPagesToRowAktPages(
   pages: number[],
   row: {
      documentSource?: string;
      annotationDocument?: { extractedPages?: number[] } | null;
      selectedPages?: number[];
      analysisItem?: { pages_kept?: number[] } | null;
   },
): number[] {
   const extracted = getAnnotationSourceExtractedPages(row);
   if (row.documentSource !== "annotation" || !extracted.length) {
      return normalizePages(pages);
   }
   return resolveAktPagesForSave({
      pdfSourceKind: "cutAnnotation",
      selectedPages: normalizePages(pages),
      sourceExtractedPages: extracted,
      cutPageCount: extracted.length,
   }).aktPages;
}

export function isServitutPdfDocumentAnnotation(item: ANNAnnotationRecord): boolean {
   const normalizedType = normalizeAnnotationTypeValue(item.annotationType as string | undefined);
   const mimeType = normalizeTextValue(item.document?.mimeType);
   const fileName = normalizeTextValue(item.document?.filename);
   const isPdf = mimeType.includes("pdf") || fileName.endsWith(".pdf");
   return (
      item.type === AnnotationType.Document &&
      (normalizedType === "dokument" || normalizedType === "servitutdokument") &&
      Boolean(item.document?.s3Key) &&
      isPdf
   );
}

function matchesDatoloebenr(item: ANNAnnotationRecord, datoloebenr?: string): boolean {
   const target = normalizeDatoloebenrValue(datoloebenr);
   if (!target) {
      return true;
   }
   return normalizeDatoloebenrValue(item.datoloebenr) === target;
}

export function findAllServitutDocumentAnnotations(
   items: ANNAnnotationRecord[],
   datoloebenr?: string,
): ANNAnnotationRecord[] {
   return items.filter((item) => isServitutPdfDocumentAnnotation(item) && matchesDatoloebenr(item, datoloebenr));
}

export function findServitutDocumentAnnotation(
   items: ANNAnnotationRecord[],
   datoloebenr?: string,
): ANNAnnotationRecord | null {
   return findAllServitutDocumentAnnotations(items, datoloebenr)[0] || null;
}

export function findStedfaestelseAnnotation(
   items: ANNAnnotationRecord[],
   datoloebenr?: string,
): ANNAnnotationRecord | null {
   const matches = items.filter((item) => (
      item.type === AnnotationType.Stedfaestelse && matchesDatoloebenr(item, datoloebenr)
   ));
   return matches.find((item) => item.verified) || matches[0] || null;
}

/** Prefer a Stedfæstelse on the same document, then one with the same dato/løbenr. */
export function pickStedfaestelseForRow(
   items: ANNAnnotationRecord[],
   options: { documentId?: string; datoloebenr?: string },
): ANNAnnotationRecord | null {
   const documentId = String(options.documentId || "");
   const sameDocument = documentId
      ? items.filter((item) => (
         item.type === AnnotationType.Stedfaestelse
         && String(item.documentId || "") === documentId
      ))
      : [];
   return findStedfaestelseAnnotation(sameDocument, options.datoloebenr)
      || findStedfaestelseAnnotation(sameDocument, undefined)
      || findStedfaestelseAnnotation(items, options.datoloebenr);
}

function parsePagesFromComment(comment?: string): number[] | null {
   if (!comment) {
      return null;
   }
   const match = comment.match(/(?:Extracted\s+)?[Pp]ages:\s*([\d,\s]+)/);
   if (!match?.[1]) {
      return null;
   }
   const pages = match[1]
      .split(/[,\s]+/)
      .map((part) => Number(part))
      .filter((page) => Number.isFinite(page) && page > 0);
   return pages.length ? normalizePages(pages) : null;
}

export function getExtractedPagesFromAnnotation(item: ANNAnnotationRecord): number[] | null {
   const fromDocument = item.document?.extractedPages;
   if (Array.isArray(fromDocument) && fromDocument.length > 0) {
      const normalized = normalizePages(fromDocument);
      return normalized.length ? normalized : null;
   }

   const historyNewestFirst = [...(item.history || [])].reverse();
   for (const entry of historyNewestFirst) {
      const pages = parsePagesFromComment(entry.comment);
      if (pages?.length) {
         return pages;
      }
   }
   return null;
}

export function stripAnalysisConfidences(item: LLMAnalysisResultItem): LLMAnalysisResultItem {
   const {
      confidence: _confidence,
      overall_confidence: _overallConfidence,
      overall_confidence_reasoning: _overallConfidenceReasoning,
      servitut_type_confidence: _servitutTypeConfidence,
      paataleberettiget_confidence: _paataleConfidence,
      case_relevance_confidence: _caseRelevanceConfidence,
      ...rest
   } = item;
   if (!rest.paataleberettiget_resolution) {
      return rest;
   }
   const { confidence: _resolutionConfidence, ...resolutionWithoutConfidence } = rest.paataleberettiget_resolution;
   return {
      ...rest,
      paataleberettiget_resolution: resolutionWithoutConfidence,
   };
}

export function buildDocumentLlmAnalysis(
   item: LLMAnalysisResultItem | undefined | null,
   options?: {
      caseDescription?: string | null;
      extractedPages?: number[];
      analyzedAt?: string;
      resultsAccepted?: boolean;
   },
): ANNDocumentLlmAnalysis | undefined {
   if (!item) {
      return undefined;
   }
   const pages = normalizePages(options?.extractedPages?.length ? options.extractedPages : (item.pages_kept || []));
   const {
      case_relevant: _caseRelevant,
      case_relevance_reasoning: _caseRelevanceReasoning,
      ...itemWithoutCaseRelevance
   } = stripAnalysisConfidences(item);
   return {
      analyzedAt: options?.analyzedAt || new Date().toISOString(),
      caseDescription: options?.caseDescription ?? item.case_description ?? null,
      resultsAccepted: Boolean(options?.resultsAccepted),
      item: {
         ...itemWithoutCaseRelevance,
         pages_kept: pages.length ? pages : (itemWithoutCaseRelevance.pages_kept || []),
         extracted_pages: pages.length || itemWithoutCaseRelevance.extracted_pages || 0,
         needs_review: options?.resultsAccepted ? false : itemWithoutCaseRelevance.needs_review,
      },
   };
}

export function mergeDocumentPayloadWithLlmAnalysis(
   document: ANNDocumentPayload,
   nextAnalysis: ANNDocumentLlmAnalysis | undefined,
   previousDocument?: ANNDocumentPayload,
): ANNDocumentPayload {
   return {
      ...document,
      llmAnalysis: nextAnalysis || previousDocument?.llmAnalysis,
      provenance: document.provenance ?? previousDocument?.provenance,
   };
}

export function getLlmAnalysisFromAnnotation(
   item: ANNAnnotationRecord | null | undefined,
): ANNDocumentLlmAnalysis | undefined {
   return item?.document?.llmAnalysis;
}

export function hydrateAnalysisItemFromAnnotation(
   item: ANNAnnotationRecord,
   rowKey: string,
): LLMAnalysisResultItem | undefined {
   const stored = getLlmAnalysisFromAnnotation(item);
   if (!stored?.item) {
      return undefined;
   }
   const pages = normalizePages(stored.item.pages_kept || []);
   const storedItem = stripAnalysisConfidences(stored.item);
   return {
      ...storedItem,
      id: rowKey,
      pages_kept: pages,
      extracted_pages: pages.length || storedItem.extracted_pages || 0,
      needs_review: stored.resultsAccepted ? false : storedItem.needs_review,
      case_description: stored.caseDescription ?? storedItem.case_description,
   };
}

export function buildOverwriteServitutTextDialogMessage(
   _existing?: ANNAnnotationRecord,
   _newPages?: number[],
): {
   header: string;
   children: string;
} {
   return {
      header: "Overwrite servitut text",
      children: Localization.getText(
         "A servitut text annotation already exists for this easement. Do you want to overwrite it?",
      ),
   };
}
